import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsers } from '../context/UserContext';
import SocioDashboard from './SocioDashboard';

const Dashboard = () => {
  const { user, isAdmin, isSocio } = useAuth();
  const { users, getUsers, loading } = useUsers();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    inactiveUsers: 0
  });

  useEffect(() => {
    const loadStats = async () => {
      const result = await getUsers(1, 1000); // Obtener todos los usuarios para estadísticas
      if (result.success && result.users) {
        const fetchedUsers = result.users;
        const totalUsers = fetchedUsers.length;
        const activeUsers = fetchedUsers.filter(u => u.isActive).length;
        const adminUsers = fetchedUsers.filter(u => u.role === 'admin').length;
        const inactiveUsers = fetchedUsers.filter(u => !u.isActive).length;

        setStats({
          totalUsers,
          activeUsers,
          adminUsers,
          inactiveUsers
        });
      }
    };

    if (isAdmin()) {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si es socio, mostrar dashboard específico para socios
  if (isSocio()) {
    return <SocioDashboard />;
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container className="mt-4">
      <div className="fade-in">
        {/* Header de bienvenida */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body className="text-center py-4">
                <h1 className="text-gradient mb-3">
                  <i className="fas fa-tachometer-alt me-3"></i>
                  ¡Bienvenido, {user?.name}!
                </h1>
                <p className="lead text-muted">
                  {isAdmin() 
                    ? 'Panel de administración del sistema COPFAMI' 
                    : 'Tu panel personal en COPFAMI'
                  }
                </p>
                <div className="mt-3">
                  <span className={`badge badge-custom ${user?.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                    <i className={`fas ${user?.role === 'admin' ? 'fa-crown' : 'fa-user'} me-1`}></i>
                    {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                  </span>
                  <span className={`badge badge-custom ms-2 ${user?.isActive ? 'bg-success' : 'bg-warning'}`}>
                    <i className={`fas ${user?.isActive ? 'fa-check-circle' : 'fa-pause-circle'} me-1`}></i>
                    {user?.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Estadísticas para administradores */}
        {isAdmin() && (
          <Row className="mb-4">
            <Col>
              <h3 className="mb-3">
                <i className="fas fa-chart-bar me-2"></i>
                Estadísticas del Sistema
              </h3>
            </Col>
          </Row>
        )}

        {isAdmin() && (
          <Row className="mb-4">
            <Col md={3} className="mb-3">
              <Card className="stats-card border-primary">
                <Card.Body>
                  <div className="stats-icon text-primary">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="stats-number text-primary">
                    {loading ? <Spinner animation="border" size="sm" /> : stats.totalUsers}
                  </div>
                  <div className="stats-label">Total Usuarios</div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3} className="mb-3">
              <Card className="stats-card border-success">
                <Card.Body>
                  <div className="stats-icon text-success">
                    <i className="fas fa-user-check"></i>
                  </div>
                  <div className="stats-number text-success">
                    {loading ? <Spinner animation="border" size="sm" /> : stats.activeUsers}
                  </div>
                  <div className="stats-label">Usuarios Activos</div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3} className="mb-3">
              <Card className="stats-card border-danger">
                <Card.Body>
                  <div className="stats-icon text-danger">
                    <i className="fas fa-crown"></i>
                  </div>
                  <div className="stats-number text-danger">
                    {loading ? <Spinner animation="border" size="sm" /> : stats.adminUsers}
                  </div>
                  <div className="stats-label">Administradores</div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3} className="mb-3">
              <Card className="stats-card border-warning">
                <Card.Body>
                  <div className="stats-icon text-warning">
                    <i className="fas fa-user-times"></i>
                  </div>
                  <div className="stats-number text-warning">
                    {loading ? <Spinner animation="border" size="sm" /> : stats.inactiveUsers}
                  </div>
                  <div className="stats-label">Usuarios Inactivos</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Acciones rápidas */}
        <Row className="mb-4">
          <Col>
            <h3 className="mb-3">
              <i className="fas fa-bolt me-2"></i>
              Acciones Rápidas
            </h3>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={6} className="mb-3">
            <Card className="card-custom h-100">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-user-circle text-primary" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-center mb-3">Mi Perfil</h5>
                <p className="text-muted text-center flex-grow-1">
                  Actualiza tu información personal, cambia tu contraseña y gestiona tu cuenta.
                </p>
                <Link to="/profile" className="btn btn-primary-custom">
                  <i className="fas fa-edit me-2"></i>
                  Ver Perfil
                </Link>
              </Card.Body>
            </Card>
          </Col>

          {isAdmin() && (
            <Col md={6} className="mb-3">
              <Card className="card-custom h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="text-center mb-3">
                    <i className="fas fa-users-cog text-success" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="text-center mb-3">Gestión de Usuarios</h5>
                  <p className="text-muted text-center flex-grow-1">
                    Administra usuarios del sistema: crear, editar, eliminar y cambiar estados.
                  </p>
                  <Link to="/users" className="btn btn-success-custom">
                    <i className="fas fa-users me-2"></i>
                    Gestionar Usuarios
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        {/* Información de la cuenta */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  Información de tu Cuenta
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>
                        <i className="fas fa-user me-2 text-primary"></i>
                        Nombre:
                      </strong>
                      <span className="ms-2">{user?.name}</span>
                    </div>
                    <div className="mb-3">
                      <strong>
                        <i className="fas fa-envelope me-2 text-primary"></i>
                        Email:
                      </strong>
                      <span className="ms-2">{user?.email}</span>
                    </div>
                    <div className="mb-3">
                      <strong>
                        <i className="fas fa-shield-alt me-2 text-primary"></i>
                        Rol:
                      </strong>
                      <span className={`badge badge-custom ms-2 ${user?.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                        {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                      </span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>
                        <i className="fas fa-calendar-plus me-2 text-primary"></i>
                        Miembro desde:
                      </strong>
                      <span className="ms-2">{user?.createdAt ? formatDate(user.createdAt) : 'N/A'}</span>
                    </div>
                    <div className="mb-3">
                      <strong>
                        <i className="fas fa-phone me-2 text-primary"></i>
                        Teléfono:
                      </strong>
                      <span className="ms-2">{user?.phone || 'No especificado'}</span>
                    </div>
                    <div className="mb-3">
                      <strong>
                        <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                        Dirección:
                      </strong>
                      <span className="ms-2">{user?.address || 'No especificada'}</span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Dashboard;